import React, { useState, useEffect } from 'react';
import { useRole } from '../context/RoleContext';
import { ShieldCheck, ShieldOff, Loader2, Info, Sparkles } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const FactCheckView = () => {
  const { isProfessor } = useRole();
  const [isEnabled, setIsEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Fetch current config on mount
  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${API_URL}/api/fact-check/config`);
      if (response.ok) {
        const data = await response.json();
        setIsEnabled(data.enabled);
      }
    } catch (err) {
      console.error('Error fetching fact check config:', err);
      setError('No se pudo cargar la configuración');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleFactCheck = async () => {
    try {
      setIsSaving(true);
      setError(null);
      setSuccessMessage(null);
      
      const newState = !isEnabled;
      
      const response = await fetch(`${API_URL}/api/fact-check/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: newState })
      });
      
      if (response.ok) {
        const data = await response.json();
        setIsEnabled(data.enabled);
        setSuccessMessage(data.enabled 
          ? '✓ Fact Check activado. Las respuestas del AI serán verificadas.'
          : '✓ Fact Check desactivado.'
        );
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        throw new Error('Error al actualizar configuración');
      }
    } catch (err) {
      console.error('Error updating fact check config:', err);
      setError('No se pudo actualizar la configuración');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isProfessor) {
    return (
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="text-center">
          <ShieldOff className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Solo los profesores pueden acceder a esta configuración</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-4 md:p-8" data-testid="fact-check-view">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500/20 to-violet-500/20 border border-blue-500/30">
              <ShieldCheck className="w-8 h-8 text-blue-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Fact Check™</h1>
              <p className="text-gray-400 text-sm">Verificación de respuestas AI</p>
            </div>
          </div>
        </div>

        {/* Main Card */}
        <div className="bg-gray-800/50 border border-gray-700/50 rounded-2xl p-6 mb-6">
          <h2 className="text-lg font-semibold text-white mb-4">Configuración</h2>
          
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-blue-400" />
              <span className="ml-2 text-gray-400">Cargando...</span>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Toggle Control */}
              <div className="flex items-center justify-between p-4 bg-gray-900/50 rounded-xl border border-gray-700/30">
                <div className="flex items-center gap-3">
                  {isEnabled ? (
                    <ShieldCheck className="w-6 h-6 text-cyan-400" />
                  ) : (
                    <ShieldOff className="w-6 h-6 text-gray-500" />
                  )}
                  <div>
                    <p className="text-white font-medium">
                      {isEnabled ? 'Fact Check Activado' : 'Fact Check Desactivado'}
                    </p>
                    <p className="text-gray-400 text-sm">
                      {isEnabled 
                        ? 'Las respuestas se verifican con documentos RAG'
                        : 'Las respuestas no se verifican'
                      }
                    </p>
                  </div>
                </div>
                
                <button
                  onClick={toggleFactCheck}
                  disabled={isSaving}
                  className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-900 ${
                    isEnabled ? 'bg-blue-500' : 'bg-gray-600'
                  } ${isSaving ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  data-testid="fact-check-toggle"
                >
                  <span
                    className={`inline-block h-6 w-6 transform rounded-full bg-white shadow-lg transition-transform duration-200 ${
                      isEnabled ? 'translate-x-7' : 'translate-x-1'
                    }`}
                  />
                  {isSaving && (
                    <Loader2 className="absolute inset-0 m-auto w-4 h-4 animate-spin text-gray-400" />
                  )}
                </button>
              </div>

              {/* Success/Error Messages */}
              {successMessage && (
                <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg text-green-400 text-sm">
                  {successMessage}
                </div>
              )}
              {error && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                  {error}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Info Section */}
        <div className="bg-gray-800/30 border border-gray-700/30 rounded-2xl p-6">
          <div className="flex items-start gap-3 mb-4">
            <Info className="w-5 h-5 text-blue-400 mt-0.5" />
            <h3 className="text-white font-medium">¿Cómo funciona?</h3>
          </div>
          
          <div className="space-y-4 text-sm text-gray-300">
            <p>
              Cuando Fact Check está activado, cada respuesta del AI es verificada automáticamente 
              contra los documentos RAG de la asignatura correspondiente.
            </p>
            
            <div className="grid gap-3">
              <div className="flex items-start gap-3 p-3 bg-gray-900/30 rounded-lg border border-cyan-500/20">
                <div className="p-1.5 rounded-lg bg-gradient-to-r from-blue-500/20 to-cyan-500/20">
                  <ShieldCheck className="w-4 h-4 text-cyan-400" />
                </div>
                <div>
                  <p className="text-cyan-300 font-medium text-sm">Fact Check™</p>
                  <p className="text-gray-400 text-xs">
                    Aparece cuando la información está verificada con el Documento de Conocimientos
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3 p-3 bg-gray-900/30 rounded-lg">
                <div className="p-1.5 rounded-lg bg-gray-700/50">
                  <Info className="w-4 h-4 text-gray-500" />
                </div>
                <div>
                  <p className="text-gray-400 font-medium text-sm">Sin insignia</p>
                  <p className="text-gray-500 text-xs">
                    Si la respuesta no contiene información del RAG o no se puede verificar, 
                    simplemente no aparecerá ninguna insignia
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-start gap-2 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg mt-4">
              <Sparkles className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
              <p className="text-blue-200 text-xs">
                <strong>Nota:</strong> La verificación solo aplica a las respuestas nuevas generadas 
                durante la sesión actual. Los mensajes históricos no serán verificados.
              </p>
            </div>

            <div className="flex items-start gap-2 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
              <Info className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
              <p className="text-amber-200 text-xs">
                <strong>Visibilidad:</strong> Tanto profesores como alumnos podrán ver la insignia 
                Fact Check™ en los mensajes verificados del AI.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FactCheckView;
